import { useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/** The blue info glyph with a hover note — the prototype's `.execute-warn`
 * icon and `.execute-warn-tip` bubble (MS-819 shared primitives, C-24; used
 * next to KPI and setting labels by MS-820/821/822).
 *
 * Geometry is the prototype's: a 16px icon 6px after its label (the one
 * label-to-icon gap, which call sites must not add to), a `--tip-bg` /
 * `--tip-fg` bubble at 11px, 6px 10px padding, 6px radius, 240px wide by
 * default, opening above the icon (`placement="below"` is the prototype's
 * `.tooltip-below`) and right-aligned to it (`align="left"` is its inline
 * `left:0;right:auto`, which moves the arrow with it). Opens on hover, as in
 * the prototype, and also on keyboard focus so the note is reachable without
 * a mouse.
 *
 * `text` may be rich (Settings › Access's permission note bolds the role
 * names, MS-997), and `className` replaces the 6px label gap where the
 * prototype sets its own (that header's inline `margin-left:4px`). */
export function InfoTip({
  text,
  placement = 'above',
  align = 'right',
  width = 240,
  className = 'ml-1.5',
}: Readonly<{
  text: ReactNode
  placement?: 'above' | 'below'
  align?: 'left' | 'right'
  width?: number
  className?: string
}>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const tipId = useId()
  const vertical = placement === 'above' ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]'
  const horizontal = align === 'right' ? 'right-0' : 'left-0'
  const arrowVertical =
    placement === 'above'
      ? 'top-full border-t-[var(--tip-bg)]'
      : 'bottom-full border-b-[var(--tip-bg)]'
  const arrowHorizontal = align === 'right' ? 'right-[10px]' : 'left-[10px]'
  return (
    <span
      className={`relative inline-flex items-center justify-center w-4 h-4 ${className} cursor-help align-middle`}
    >
      <button
        type="button"
        aria-label={t('common.moreInfo')}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 p-0 bg-transparent border-0 cursor-help"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="var(--info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>
      {open && (
        <span
          id={tipId}
          role="tooltip"
          style={{ width }}
          className={`absolute ${vertical} ${horizontal} z-[100] bg-[var(--tip-bg)] text-[var(--tip-fg)] text-[11px] font-normal normal-case tracking-normal leading-[1.4] whitespace-normal px-2.5 py-1.5 rounded-md [box-shadow:var(--shadow-md)]`}
        >
          {text}
          <span
            aria-hidden="true"
            className={`absolute ${arrowVertical} ${arrowHorizontal} border-[5px] border-transparent`}
          />
        </span>
      )}
    </span>
  )
}
